import { CaseExport, ExportStatus } from "generated/graphql";
import { Box, Tooltip } from "@mui/material";
import theme, { EllipsisSX } from "theme";
import { P } from "theme/typography";
import { displayDateAndTimeCompact, displayMimeType, INTERNAL_USER_NAME } from "Backend";
import { fullName } from "helpers/name-helpers";
import formatSizeBytes from "helpers/formatSizeBytes";
import SimpleMenu, { SimpleMenuItem } from "components/SimpleMenu";
import {
  CheckCircle,
  Delete,
  Download,
  ErrorOutlineOutlined,
  HourglassTop,
  NotInterested,
  Share,
} from "@mui/icons-material";
import { useContext, useState } from "react";
import { deleteCaseExport, updateCaseExportCollectSharedState } from "redux/slices/case";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useAppSelector } from "redux/hooks";
import { selectCollectEnabled } from "redux/slices/session";
import { triggerCaseExportDownload } from "./CaseExports";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { stripNull } from "helpers/coll";

const CollectURL = new URL("./collect_24.png", import.meta.url).href;

export type CaseExportItemProps = {
  exp: CaseExport;
  isLastItem: boolean;
  caseId: number;
};

export function ExportDisplayStatus({ status }: { status: ExportStatus }) {
  return status === ExportStatus.Complete ? (
    <Tooltip title="Export Complete">
      <CheckCircle data-on-hover="show" sx={{ color: theme.palette.grey[300] }} />
    </Tooltip>
  ) : status === ExportStatus.Requested ? (
    <Tooltip title="Export In Progress" arrow>
      <HourglassTop data-on-hover="show" sx={{ color: theme.palette.grey[100] }} />
    </Tooltip>
  ) : status === ExportStatus.Failed ? (
    <Tooltip title="Export Failed" arrow>
      <ErrorOutlineOutlined data-on-hover="show" sx={{ color: theme.palette.error.main }} />
    </Tooltip>
  ) : (
    <></>
  );
}

const CaseExportItem = ({ exp, isLastItem, caseId }: CaseExportItemProps) => {
  const snackpatch = useAppDispatchWithSnackbar();
  const confirmCtx = useContext(ConfirmationDialogContext);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const collectEnabled = useAppSelector(selectCollectEnabled);
  const exportComplete = exp.status === ExportStatus.Complete;
  const exportFailed = exp.status === ExportStatus.Failed;
  const createdBy = stripNull(exp.createdBy);
  const handleDeleteCaseExport = async () => {
    setIsDeleting(true);
    await snackpatch(deleteCaseExport({ caseExportId: exp.id }), {
      successMessage: "Export deleted",
      errorMessage: "Failed to delete export",
    });
    setIsDeleting(false);
  };
  const toggleShareToCollect = async () => {
    setIsSharing(true);
    await snackpatch(
      updateCaseExportCollectSharedState({
        caseId,
        caseExportId: exp.id,
        share: !exp.collectShared,
      }),
      {
        successMessage: `Export ${!exp.collectShared ? "shared" : "no longer shared"}`,
        errorMessage: "Failed to update",
      }
    );
    setIsSharing(false);
  };

  const menuItems: SimpleMenuItem[] = (() => {
    const items: SimpleMenuItem[] = [];
    if (exportComplete) {
      items.push({
        icon: <Download />,
        name: "Download",
        action: () => triggerCaseExportDownload(exp.caseId, exp.id),
      });
    }
    if (collectEnabled && exportComplete) {
      items.push({
        icon: exp.collectShared ? <NotInterested /> : <Share />,
        name: exp.collectShared ? "Stop sharing with Collect" : "Share with Collect",
        disabled: isSharing,
        action: toggleShareToCollect,
      });
    }
    items.push({
      icon: <Delete />,
      name: "Delete",
      disabled: isDeleting,
      action: () => {
        confirmCtx.confirm({
          title: "Delete Export",
          prompt: (
            <>
              <P variant="body2">Are you sure you want to delete the following export?</P>
              <P>
                {exp.exportType} ({exp.fileName})
              </P>
            </>
          ),
          callback: () => {
            return handleDeleteCaseExport();
          },
          confirmButtonText: "Delete",
        });
      },
    });

    return items;
  })();

  return (
    <Box
      key={exp.id}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: isLastItem ? "none" : `1px solid ${theme.palette.grey[50]}`,
        p: 1,
      }}>
      {/* The following is not supposed to push the menu out of the box but is */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "80%",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", color: "grey.500", gap: 1 }}>
          {exportFailed && (
            <Tooltip title="Export Failed" arrow>
              <ErrorOutlineOutlined data-on-hover="show" sx={{ color: theme.palette.error.main }} />
            </Tooltip>
          )}
          <P variant="subtitle3">{exp.exportType}</P>
        </Box>
        <P variant="body5">
          {displayDateAndTimeCompact(exp.createdAt)} by{" "}
          {createdBy !== undefined ? fullName(createdBy) : INTERNAL_USER_NAME} • {displayMimeType(exp.contentType)}
        </P>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: "grey.500",
          }}>
          <P variant="body5" sx={EllipsisSX} title={exp.fileName || undefined}>
            {exp.fileName}
            {exp.sizeBytes ? ` (${formatSizeBytes(exp.sizeBytes)})` : ``}
          </P>
        </Box>
        {exp.collectShared ? (
          <Box sx={{ display: "flex", alignItems: "center", color: "grey.500" }}>
            <img src={CollectURL} />
            <P sx={{ ml: 1 }} variant="body5">
              Shared with Collect
            </P>
          </Box>
        ) : (
          <></>
        )}
      </Box>
      <Box>
        {exportComplete || exportFailed ? (
          <SimpleMenu items={menuItems} meatball />
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <ExportDisplayStatus status={exp.status} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CaseExportItem;
